<template>
  <div class="app-topbar-wrapper">
    <!-- Línea 1: Brand + metadata + theme + user + logout -->
    <header class="app-topbar">
      <div class="topbar-left">
        <span class="brand">
          <span class="brand-dot">●</span>
          <span class="brand-name">KOMPARA<span class="brand-slash">/</span><span class="brand-client">VSVIP</span></span>
        </span>
        <span class="meta-item" v-if="lastScrape">
          <span class="meta-label">LAST SCRAPE</span>
          <span class="meta-value">{{ formatScrapeDate(lastScrape) }}</span>
        </span>
        <span class="meta-item" v-if="coverage !== null && coverage !== undefined">
          <span class="meta-label">COVERAGE</span>
          <span class="meta-value">{{ coverage.toLocaleString() }}</span>
        </span>
      </div>
      <div class="topbar-right">
        <span class="status-online">● ONLINE</span>
        <button @click="toggleTheme" class="topbar-btn" :title="theme === 'dark' ? 'Modo claro' : 'Modo oscuro'">
          <component :is="theme === 'dark' ? PhSun : PhMoon" :size="16" weight="bold" />
        </button>
        <span class="username">{{ username }}</span>
        <button v-if="showLogout" @click="$emit('logout')" class="topbar-btn logout-btn" title="Cerrar sesión">
          <PhPower :size="16" weight="bold" />
        </button>
      </div>
    </header>

    <!-- Línea 2: Tabs de navegación + vista toggle + version -->
    <nav class="app-tabs">
      <div class="tabs-left">
        <router-link to="/" class="tab" :class="{ active: $route.name === 'home' }">HOME</router-link>
        <router-link to="/alertas" class="tab" :class="{ active: $route.name === 'alertas' }">ALERTAS</router-link>
        <router-link to="/analisis" class="tab" :class="{ active: $route.name === 'analisis' }">ANÁLISIS</router-link>

        <!-- Heatmaps con dropdown de guardados -->
        <div class="tab-dropdown" @mouseleave="dropdownOpen = false">
          <router-link
            to="/heatmaps"
            class="tab"
            :class="{ active: $route.name === 'heatmaps' || $route.name === 'heatmap-view' }"
            @mouseenter="dropdownOpen = true"
          >
            HEATMAPS
            <PhCaretDown :size="11" weight="bold" class="dropdown-caret" />
          </router-link>
          <div v-if="dropdownOpen && savedHeatmaps && savedHeatmaps.length > 0" class="dropdown-menu" @mouseenter="dropdownOpen = true">
            <router-link
              v-for="hm in savedHeatmaps.filter(h => h.is_active)"
              :key="hm.id"
              :to="`/heatmap/${hm.id}`"
              class="dropdown-item"
              @click="dropdownOpen = false"
            >
              <span class="hm-name">{{ hm.name }}</span>
              <span class="hm-meta">{{ hm.product_count }} productos</span>
            </router-link>
            <router-link
              to="/heatmaps"
              class="dropdown-item dropdown-footer"
              @click="dropdownOpen = false"
            >
              Ver todos los guardados →
            </router-link>
          </div>
        </div>

        <router-link
          v-if="showAdmin"
          to="/admin/usuarios"
          class="tab"
          :class="{ active: $route.name && $route.name.startsWith('admin') }"
        >
          ADMIN
        </router-link>
      </div>

      <div class="tabs-right">
        <!-- Toggle vista web/marketplace -->
        <div v-if="showVistaToggle" class="vista-switcher">
          <span class="vista-label">VISTA</span>
          <button
            @click="$emit('change-vista', 'marketplace')"
            class="vista-btn"
            :class="{ active: vistaActual === 'marketplace' }"
          >
            MARKETPLACE
            <span class="vista-count">{{ conteoMarketplace }}</span>
          </button>
          <button
            @click="$emit('change-vista', 'web')"
            class="vista-btn"
            :class="{ active: vistaActual === 'web' }"
          >
            WEB PROPIA
            <span class="vista-count">{{ conteoWeb }}</span>
          </button>
        </div>

        <span class="version">v5.0</span>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useTheme } from '../composables/useTheme';
import { useScrapeMeta } from '../composables/useScrapeMeta';
import { useSavedHeatmaps } from '../composables/useSavedHeatmaps';
import { PhSun, PhMoon, PhPower, PhCaretDown } from '@phosphor-icons/vue';

defineProps({
  username: { type: String, default: 'admin' },
  showAdmin: { type: Boolean, default: false },
  showLogout: { type: Boolean, default: true },
  showVistaToggle: { type: Boolean, default: false },
  vistaActual: { type: String, default: 'web' },
  conteoMarketplace: { type: Number, default: 0 },
  conteoWeb: { type: Number, default: 0 },
});

defineEmits(['logout', 'change-vista']);

const { theme, toggleTheme } = useTheme();
const { lastScrape, coverage } = useScrapeMeta();
const { savedHeatmaps } = useSavedHeatmaps();

const dropdownOpen = ref(false);

const formatScrapeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};
</script>

<style scoped>
.app-topbar-wrapper {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

/* ============ LÍNEA 1: TOPBAR ============ */
.app-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  font-family: var(--font-mono);
  font-size: 11px;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  letter-spacing: 0.05em;
}
.brand-dot {
  color: var(--color-accent);
  font-size: 10px;
}
.brand-name {
  color: var(--color-text);
  text-transform: uppercase;
}
.brand-slash {
  color: var(--color-text-faint);
  margin: 0 1px;
}
.brand-client {
  color: var(--color-accent);
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.meta-label {
  color: var(--color-text-faint);
  font-weight: 600;
  letter-spacing: 0.08em;
}
.meta-value {
  color: var(--color-text);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.status-online {
  color: var(--color-success);
  font-weight: 700;
  letter-spacing: 0.08em;
}
.username {
  color: var(--color-text-muted);
  letter-spacing: 0.05em;
}

.topbar-btn {
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  width: 28px;
  height: 28px;
  border-radius: 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.topbar-btn:hover {
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}
.logout-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}

/* ============ LÍNEA 2: TABS ============ */
.app-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  background: var(--color-surface);
  font-family: var(--font-mono);
  min-height: 36px;
}

.tabs-left {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.tab {
  padding: 9px 16px;
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.tab:hover {
  color: var(--color-text);
}
.tab.active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
  background: var(--color-accent-bg);
}

.tab-dropdown {
  position: relative;
}
.dropdown-caret {
  margin-left: 2px;
  opacity: 0.7;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 280px;
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 0 0 3px 3px;
  box-shadow: var(--shadow-lg);
  z-index: 200;
  overflow: hidden;
}
.dropdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  color: var(--color-text);
  text-decoration: none;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  transition: background 0.1s;
  font-family: 'Inter', sans-serif;
}
.dropdown-item:hover {
  background: var(--color-hover-row);
  color: var(--color-accent);
}
.dropdown-item:last-child {
  border-bottom: none;
}
.hm-name {
  font-weight: 600;
}
.hm-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-faint);
  letter-spacing: 0.03em;
}
.dropdown-footer {
  background: var(--color-surface-3);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-accent) !important;
  justify-content: center;
}

/* ============ TABS RIGHT ============ */
.tabs-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.vista-switcher {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.vista-switcher .vista-label {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-text-faint);
}
.vista-btn {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.vista-btn:hover {
  color: var(--color-text);
}
.vista-btn.active {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}
.vista-count {
  font-size: 8.5px;
  background: var(--color-surface-3);
  padding: 1px 5px;
  border-radius: 2px;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
}
.vista-btn.active .vista-count {
  background: var(--color-accent);
  color: var(--color-surface-3);
  opacity: 1;
}

.version {
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--color-text-faint);
  letter-spacing: 0.05em;
}
</style>